import React, { useContext } from 'react';
import { Box, Typography, TextField, InputAdornment, Button, IconButton, Tabs, Tab, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HelpContext from '../../contexts/HelpContext';
import useDesignTokens from './useDesignTokens';
import { layout } from '../../themes/smartinventoryTheme';

// title + subtitle left; help button (when `helpKey` is set), search,
// secondary (outlined) actions and ONE primary (contained) action right.
// Stacks actions under the title below 900px. No breadcrumbs here — those
// live in AppShell, detail routes only. `meter` is an optional node
// (typically <FeatureUsageMeter />) shown on its own row under the title —
// this page's own plan limit, in place, without a trip to the Subscription
// page. Omit `helpKey`/`meter` and nothing about this component changes for
// every other caller.
//
// `helpKey` opens the in-view help panel (step 9) to that route's topics.
// It reads HelpContext directly (not the throwing `useHelp()` hook) because
// PageHeader renders on pages outside any HelpProvider too (login, the
// customer shell, the dev design-system preview) — those simply never pass
// `helpKey`, so the button never renders, but the hook call itself must
// still be safe everywhere. The role check below additionally covers a page
// like Subscription.js that renders in BOTH the admin and customer shells —
// HelpProvider wraps every shell (so the hook is never unsafe to call), but
// only the admin/GA shells also mount the HelpPanel itself, so a customer
// role never gets a help button whose panel doesn't actually exist there.
const PageHeader = ({ title, subtitle, search, actions = [], tabs, meter, helpKey }) => {
  const helpCtx = useContext(HelpContext);
  const { tokens } = useDesignTokens();
  const { t: tHelp } = useTranslation('help');
  const showHelpButton = Boolean(helpKey && helpCtx && localStorage.getItem('userRole') !== 'user');
  const helpLabel = tHelp('panel.title', { defaultValue: 'Help' });

  return (
  <Box sx={{ mb: 3 }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
      }}
    >
      <Box>
        <Typography variant="h4">{title}</Typography>
        {subtitle && <Typography variant="body1" color="text.secondary">{subtitle}</Typography>}
      </Box>
      {(search || actions.length > 0 || showHelpButton) && (
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
          {showHelpButton && (
            <Tooltip title={helpLabel}>
              <IconButton
                aria-label={helpLabel}
                data-testid="page-header-help-button"
                onClick={() => helpCtx.open()}
                sx={{
                  width: layout.helpButtonSize,
                  height: layout.helpButtonSize,
                  bgcolor: tokens.accentTintStrong,
                  border: `1px solid ${tokens.accentBorder}`,
                  color: 'primary.main',
                  '&:hover': { bgcolor: tokens.accentTintStrong },
                }}
              >
                <HelpOutlineIcon sx={{ fontSize: 19 }} />
              </IconButton>
            </Tooltip>
          )}
          {search && (
            <TextField
              value={search.value}
              onChange={search.onChange}
              placeholder={search.placeholder}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: '100%', sm: 230 } }}
            />
          )}
          {actions.map((action) => {
            const button = (
              <Button
                variant={action.variant || 'outlined'}
                startIcon={action.icon ? <action.icon fontSize="small" /> : undefined}
                onClick={action.onClick}
                disabled={action.disabled}
                data-tour={action.tourId}
              >
                {action.label}
              </Button>
            );
            return action.disabled && action.disabledReason ? (
              <Tooltip key={action.label} title={action.disabledReason}>
                <span>{button}</span>
              </Tooltip>
            ) : (
              <React.Fragment key={action.label}>{button}</React.Fragment>
            );
          })}
        </Box>
      )}
    </Box>
    {meter && <Box sx={{ mt: 2, maxWidth: { xs: '100%', sm: 260 } }}>{meter}</Box>}
    {tabs && (
      <Tabs value={tabs.value} onChange={tabs.onChange} sx={{ mt: 2 }}>
        {tabs.items.map((item) => (
          <Tab key={item.value} value={item.value} label={item.label} />
        ))}
      </Tabs>
    )}
  </Box>
  );
};

export default PageHeader;
